'use client';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Button } from '@tuic/web-compositions/core/Button';
import { LoadingSpinner } from '@tuic/web-compositions/core/LoadingSpinner';
import { HeaderSection } from '@tuic/web-compositions/shared/HeaderSection';
import { TextButton } from '@tuic/web-compositions/core/TextButton';
import { useClientTranslation } from '@lib/i18n/client';
import { PhaserClientData } from '@modules/phaser';
import { Locale, TFunction } from '@tuic/i18n';
import { StoredDismissedAlert } from '@modules/alerts';
import { Token } from '@lib/token';
import {
  alertServerStorageActionDelete,
  alertServerStorageActionFlush,
  alertServerStorageActionGet,
} from '@modules/alerts/actions';
import { DATE_TIME_OPTION_SHORT_NUMERIC } from '@lib/date';
import { DateTime } from 'luxon';
import {
  createAlertClientStorage,
  getComparisonKey,
  uniqueAlerts,
} from '@modules/alerts/client';
import { getAlertTitles } from '@modules/phaser/actions/alerts';

type Props = {
  data: PhaserClientData;
  token: Token;
  locale: Locale;
};

type AlertWithStorageOrigin = StoredDismissedAlert & {
  storageOrigin: 'client' | 'remote' | 'both';
};

type ResetButtonProps = {
  alert: AlertWithStorageOrigin;
  onReset: (alert: AlertWithStorageOrigin) => Promise<void>;
};

export const DismissedAlerts: FC<Props> = ({ data, token, locale }) => {
  let { t } = useClientTranslation();
  let [pending, setPending] = useState(false);

  let [remoteAlerts, setRemoteAlerts] = useState<AlertWithStorageOrigin[]>();
  let [clientAlerts, setClientAlerts] = useState<AlertWithStorageOrigin[]>();

  let [alertTitles, setAlertTitles] = useState<Record<string, string>>({});

  let alertClientStorage = useMemo(() => createAlertClientStorage(), []);

  let combinedAlerts = useMemo((): AlertWithStorageOrigin[] => {
    let remoteAsArray = remoteAlerts ?? [];
    let clientAsArray = clientAlerts ?? [];

    let remoteKeys = remoteAsArray.map(getComparisonKey);
    let clientKeys = clientAsArray.map(getComparisonKey);

    let combined = [...remoteAsArray, ...clientAsArray].sort((a, b) => {
      return a.dismissedAt.toUnixInteger() - b.dismissedAt.toUnixInteger();
    });

    return uniqueAlerts(combined)
      .sort((a, b) => {
        return b.dismissedAt.toUnixInteger() - a.dismissedAt.toUnixInteger();
      })
      .map(alert => {
        let key = getComparisonKey(alert);
        let isRemote = remoteKeys.includes(key);
        let isClient = clientKeys.includes(key);

        if (isRemote && isClient) {
          return {
            ...alert,
            storageOrigin: 'both',
          };
        }

        return {
          ...alert,
          storageOrigin: isRemote ? 'remote' : 'client',
        };
      });
  }, [remoteAlerts, clientAlerts]);

  let handleUpdate = useCallback(async () => {
    let [remote, client] = await Promise.all([
      alertServerStorageActionGet(token),
      alertClientStorage.get(),
    ]);

    let remoteWithOrigin = remote.map((alert): AlertWithStorageOrigin => {
      let dismissedAt = DateTime.fromISO(alert.dismissedAt).toLocal();

      if (!dismissedAt.isValid) {
        dismissedAt = DateTime.now();
      }

      return {
        ...alert,
        dismissedAt,
        storageOrigin: 'remote',
      };
    });

    let clientWithOrigin = client.map(
      (alert): AlertWithStorageOrigin => ({
        ...alert,
        storageOrigin: 'client',
      }),
    );

    setRemoteAlerts(remoteWithOrigin);
    setClientAlerts(clientWithOrigin);
  }, [token, alertClientStorage]);

  let dataFetched = typeof remoteAlerts !== 'undefined';

  let handleSingleAlertReset = useCallback(
    async (alert: AlertWithStorageOrigin) => {
      let isRemote =
        alert.storageOrigin === 'both' || alert.storageOrigin === 'remote';
      let isClient =
        alert.storageOrigin === 'both' || alert.storageOrigin === 'client';

      await Promise.all([
        isRemote ? alertServerStorageActionDelete(token, alert) : undefined,
        isClient ? alertClientStorage.delete(alert) : undefined,
      ]);

      await handleUpdate();
    },
    [alertClientStorage, handleUpdate, token],
  );

  let handleResettingAllAlerts = useCallback(async () => {
    setPending(true);

    await Promise.all([
      alertServerStorageActionFlush(token),
      alertClientStorage.flush(),
    ]);

    await handleUpdate();

    setPending(false);
  }, [alertClientStorage, handleUpdate, token]);

  useEffect(() => {
    handleUpdate();
  }, [handleUpdate, alertClientStorage]);

  useEffect(() => {
    let ids = combinedAlerts.map(alert => alert.id);

    if (ids.length === 0) {
      return;
    }

    getAlertTitles(locale, ids).then(titles => {
      setAlertTitles(titles);
    });
  }, [locale, combinedAlerts]);

  return (
    <div className="space-y-6">
      <HeaderSection
        disableSublineWidthRestriction
        headline={t('Geschlossene Hinweise')}
        subline={
          <div className="space-y-4 sm:space-y-0 sm:flex gap-6">
            <div>
              {t(
                'Sie finden in dieser Rubrik eine Liste an Hinweisen die Sie mit Ihrem Nutzer-Konto oder mit Ihrem Endgerät bereits geschlossen haben.',
              )}
            </div>
            <div className="flex-none">
              <Button
                variant="secondary"
                indicating={pending}
                disabled={
                  pending || !dataFetched || combinedAlerts.length === 0
                }
                onClick={handleResettingAllAlerts}
              >
                {t('Alle Hinweise zurücksetzen')}
              </Button>
            </div>
          </div>
        }
      />

      {!dataFetched && (
        <div className="text-center">
          <LoadingSpinner size="md" className="inline-block" />
        </div>
      )}

      {dataFetched && combinedAlerts.length === 0 && (
        <Alert mode="information-gray">
          {t(
            'In Ihrem Konto oder auf Ihrem Endgerät befinden sich keine Hinweise, die Sie zuvor geschlossen haben.',
          )}
        </Alert>
      )}

      {dataFetched && combinedAlerts && combinedAlerts.length > 0 && (
        <table className="table-fixed w-full text-base rounded-md overflow-hidden">
          <thead className="headline-xs bg-ocean-40 text-left">
            <tr className="[&_th]:px-4 [&_th]:py-2 [&_th]:font-semibold">
              <th className="w-[33%]">{t('Hinweis')}</th>
              <th>{t('Speicherort')}</th>
              <th>{t('Hinweis-Ort')}</th>
              <th className="w-[21%]">{t('Zuletzt geschlossen am')}</th>
              <th className="w-[150px]" />
            </tr>
          </thead>
          <tbody>
            {combinedAlerts.map(alert => (
              <tr
                key={`${alert.id}.${alert.origin}.${alert.storageOrigin}`}
                className="bg-white border-b border-stroke-secondary-20 last:border-b-0 [&_td]:px-4 [&_td]:py-2"
              >
                <td>
                  {renderAlertId(t, data.contentful, alertTitles, alert.id)}
                </td>
                <td>{renderStorageOrigin(t, alert)}</td>
                <td>{renderOrigin(t, alert)}</td>
                <td>{renderDate(locale, alert)}</td>
                <td>
                  <ResetButton onReset={handleSingleAlertReset} alert={alert} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ResetButton: FC<ResetButtonProps> = ({ alert, onReset }) => {
  let { t } = useClientTranslation();
  let [pending, setPending] = useState(false);

  let handleClick = useCallback(async () => {
    setPending(true);

    await onReset(alert);

    setPending(false);
  }, [alert, onReset]);

  return (
    <TextButton
      iconLeft={pending ? 'loading' : 'clock-rotate-right'}
      disabled={pending}
      iconSize="xs"
      size="sm"
      onClick={handleClick}
    >
      {t('Zurücksetzen')}
    </TextButton>
  );
};

const renderOrigin = (t: TFunction, alert: StoredDismissedAlert) => {
  let asNumber = Number(alert.origin);

  if (!isNaN(asNumber)) {
    return `#${alert.origin}`;
  }

  let letters = alert.origin.split('');
  letters[0] = letters[0].toUpperCase();

  return letters.join('');
};

const renderDate = (locale: Locale, alert: StoredDismissedAlert) => {
  return alert.dismissedAt.toLocaleString(DATE_TIME_OPTION_SHORT_NUMERIC, {
    locale,
  });
};

const renderStorageOrigin = (t: TFunction, alert: AlertWithStorageOrigin) => {
  if (alert.storageOrigin === 'both') {
    return t('Konto & Endgerät');
  }

  return alert.storageOrigin === 'remote' ? t('Konto') : t('Endgerät');
};

const renderAlertId = (
  t: TFunction,
  contentful: PhaserClientData['contentful'],
  titles: Record<string, string>,
  id: string,
) => {
  if (id === '') {
    return <span className="text-marine-40 italic">{t('Leerer Wert')}</span>;
  }

  if (!contentful) {
    return id;
  }

  let url = `https://app.contentful.com/spaces/${contentful.space}/environments/${contentful.environment}/entries/${id}`;

  return (
    <a href={url} target="_blank" className="underline hover:no-underline">
      {titles[id] ?? id}
    </a>
  );
};
